import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Row, Col, Table, DatePicker, Typography, Alert, Spin, Button, Statistic } from 'antd';
import dayjs from 'dayjs';
import { 
  FileTextOutlined,
  HomeOutlined,
  FilePdfOutlined,
  FileExcelOutlined,
  PrinterOutlined,
  ShoppingCartOutlined,
  BarChartOutlined,
  RiseOutlined,
  LogoutOutlined
} from '@ant-design/icons';
import { Bar, Pie, Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  LineElement,
  PointElement,
  Filler
} from 'chart.js';
import axios from 'axios';
import { getHeaders } from '../utility/getHeader';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import fetchData from '../functions/fetchData';
import './ClubStoreManagerDashboard.css';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  LineElement,
  PointElement,
  Filler
);

const { Text } = Typography;
const { RangePicker } = DatePicker;

const ClubStoreManagerDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [barSalesData, setBarSalesData] = useState([]);
  const [dateRange, setDateRange] = useState([dayjs(), dayjs()]);
  const [error, setError] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [totalQty, setTotalQty] = useState(0);

  // Get user information from localStorage/sessionStorage
  const userType = (localStorage.getItem('usertype') || sessionStorage.getItem('usertype') || '').toLowerCase();
  const userId = localStorage.getItem('userid') || sessionStorage.getItem('userid') || localStorage.getItem('uname') || sessionStorage.getItem('uname');
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');

  // Check if user is authenticated and is a club store manager
  const isClubStoreManager = userType && (userType === 'cstoremanager' || userType === 'club_store_manager');
  const isAuthenticated = !!token;

  const fetchBarSalesData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (!dateRange || dateRange.length !== 2 || !dateRange[0] || !dateRange[1]) {
        setError('Please select a valid date range');
        setLoading(false);
        return;
      }

      const from = dateRange[0].format('YYYY-MM-DD');
      const to = dateRange[1].format('YYYY-MM-DD');

      // Use fetchData function with bar_cart table
      const whereCondition = {
        dte: from,
        dte: to
      };

      const data = await fetchData('bar_cart', null, 'id DESC', whereCondition);

      if (data && data.length > 0) {
        setBarSalesData(data);
        
        // Calculate total quantity
        const total = data.reduce((sum, item) => sum + parseFloat(item.quantity || 0), 0);
        setTotalQty(total);
        
        toast.success('Bar sales data loaded successfully');
      } else {
        setBarSalesData([]);
        setTotalQty(0);
        toast.info('No data found for the selected date range');
      }
    } catch (err) {
      console.error('Error fetching bar sales data:', err);
      setError('Failed to load bar sales data. Please try again.');
      toast.error('Error loading bar sales data: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    // Check authentication first
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    // Check if user is club store manager
    if (!isClubStoreManager) {
      setAccessDenied(true);
      setLoading(false);
      return;
    }

    // If authenticated and is club store manager, load data
    if (userId && dateRange) {
      fetchBarSalesData();
    }
  }, [isAuthenticated, isClubStoreManager, userId, navigate, dateRange, fetchBarSalesData]);

  const handleDateRangeChange = (dates) => {
    if (dates && dates.length === 2) {
      setDateRange(dates);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    toast.success('Logged out successfully!');
    navigate('/logout');
  };

  // Analytics calculations
  const analytics = useMemo(() => {
    if (!barSalesData || barSalesData.length === 0) {
      return {
        topItems: [],
        categoryData: [],
        totalItems: 0,
        uniqueItems: 0
      };
    }

    // Get top 10 items by quantity
    const itemsMap = {};
    barSalesData.forEach(item => {
      const name = item.item_name || 'Unknown';
      const qty = parseFloat(item.quantity || 0);
      
      if (itemsMap[name]) {
        itemsMap[name] += qty;
      } else {
        itemsMap[name] = qty;
      }
    });

    const topItems = Object.entries(itemsMap)
      .map(([name, quantity]) => ({ name, quantity }))
      .sort((a, b) => b.quantity - a.quantity)
      .slice(0, 10);

    // Category breakdown
    const categoryMap = {};
    barSalesData.forEach(item => {
      const category = item.category || 'Other';
      const qty = parseFloat(item.quantity || 0);
      
      if (categoryMap[category]) {
        categoryMap[category] += qty;
      } else {
        categoryMap[category] = qty;
      }
    });

    const categoryData = Object.entries(categoryMap)
      .map(([category, quantity]) => ({ category, quantity }));

    // Daily sales trend
    const dailySales = {};
    barSalesData.forEach(item => {
      const date = item.dte || 'Unknown';
      const qty = parseFloat(item.quantity || 0);
      
      if (dailySales[date]) {
        dailySales[date] += qty;
      } else {
        dailySales[date] = qty;
      }
    });

    const dailyData = Object.entries(dailySales)
      .map(([date, quantity]) => ({ date, quantity }))
      .sort((a, b) => new Date(a.date) - new Date(b.date));

    return {
      topItems,
      categoryData,
      dailyData,
      totalItems: barSalesData.length,
      uniqueItems: Object.keys(itemsMap).length
    };
  }, [barSalesData]);

  // Chart data for top items
  const topItemsChartData = {
    labels: analytics.topItems.map(item => item.name.length > 20 ? item.name.substring(0, 20) + '...' : item.name),
    datasets: [
      {
        label: 'Quantity Sold',
        data: analytics.topItems.map(item => item.quantity),
        backgroundColor: 'rgba(54, 162, 235, 0.6)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1
      }
    ]
  };

  // Chart data for categories
  const categoryChartData = {
    labels: analytics.categoryData.map(item => item.category),
    datasets: [
      {
        label: 'Quantity by Category',
        data: analytics.categoryData.map(item => item.quantity),
        backgroundColor: [
          'rgba(255, 99, 132, 0.6)',
          'rgba(54, 162, 235, 0.6)',
          'rgba(255, 206, 86, 0.6)',
          'rgba(75, 192, 192, 0.6)',
          'rgba(153, 102, 255, 0.6)',
          'rgba(255, 159, 64, 0.6)'
        ],
        borderColor: [
          'rgba(255, 99, 132, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(255, 206, 86, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(153, 102, 255, 1)',
          'rgba(255, 159, 64, 1)'
        ],
        borderWidth: 1
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top'
      },
      title: {
        display: false
      }
    },
    scales: {
      y: {
        beginAtZero: true
      }
    }
  };

  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right'
      }
    }
  };

  // Chart data for daily sales trend
  const dailySalesTrendData = {
    labels: analytics.dailyData?.map(item => {
      const date = new Date(item.date);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }) || [],
    datasets: [
      {
        label: 'Daily Sales Quantity',
        data: analytics.dailyData?.map(item => item.quantity) || [],
        borderColor: 'rgba(75, 192, 192, 1)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        fill: true,
        tension: 0.4
      }
    ]
  };

  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top'
      }
    },
    scales: {
      y: {
        beginAtZero: true
      }
    }
  };

  const exportToPDF = () => {
    try {
      if (!dateRange || dateRange.length !== 2 || !dateRange[0] || !dateRange[1]) {
        toast.error('Please select a valid date range first');
        return;
      }

      const doc = new jsPDF();
      const from = dateRange[0].format('YYYY-MM-DD');
      const to = dateRange[1].format('YYYY-MM-DD');
      
      doc.setFontSize(16);
      doc.text('Bar Sales Report', 14, 15);
      doc.setFontSize(10);
      doc.text(`Date Range: ${from} to ${to}`, 14, 25);
      doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 32);

      const tableData = barSalesData.map((item, index) => [
        index + 1,
        item.name || '-',
        parseFloat(item.qty || 0).toFixed(2)
      ]);

      doc.autoTable({
        head: [['S.No.', 'Item Name', 'Quantity']],
        body: tableData,
        startY: 40,
        headStyles: {
          fillColor: [41, 128, 185],
          textColor: [255, 255, 255],
          fontStyle: 'bold'
        },
        bodyStyles: {
          textColor: [50, 50, 50]
        },
        footStyles: {
          fillColor: [230, 230, 230],
          textColor: [0, 0, 0],
          fontStyle: 'bold'
        },
        foot: [['Total', 'Qty', totalQty.toFixed(2)]]
      });

      doc.save(`bar_sales_${from}_to_${to}.pdf`);
      toast.success('PDF exported successfully');
    } catch (err) {
      console.error('Error exporting PDF:', err);
      toast.error('Error exporting PDF');
    }
  };

  const exportToExcel = () => {
    try {
      if (!dateRange || dateRange.length !== 2 || !dateRange[0] || !dateRange[1]) {
        toast.error('Please select a valid date range first');
        return;
      }

      const from = dateRange[0].format('YYYY-MM-DD');
      const to = dateRange[1].format('YYYY-MM-DD');

      const dataToExport = barSalesData.map((item, index) => ({
        'S.No.': index + 1,
        'Item Name': item.name || '-',
        'Quantity': parseFloat(item.qty || 0).toFixed(2)
      }));

      // Add total row
      dataToExport.push({
        'S.No.': '',
        'Item Name': 'Total',
        'Quantity': totalQty.toFixed(2)
      });

      const worksheet = XLSX.utils.json_to_sheet(dataToExport);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Bar Sales');
      XLSX.writeFile(workbook, `bar_sales_${from}_to_${to}.xlsx`);
      
      toast.success('Excel exported successfully');
    } catch (err) {
      console.error('Error exporting Excel:', err);
      toast.error('Error exporting Excel');
    }
  };

  const printThermal = () => {
    try {
      if (!dateRange || dateRange.length !== 2 || !dateRange[0] || !dateRange[1]) {
        toast.error('Please select a valid date range first');
        return;
      }

      const from = dateRange[0].format('YYYY-MM-DD');
      const to = dateRange[1].format('YYYY-MM-DD');
      const now = new Date();
      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const dayName = dayNames[now.getDay()];
      
      let printContent = `
        <div style="font-family: monospace; font-size: 12px; width: 80mm; margin: 0 auto;">
          <div style="text-align: center; border-bottom: 1px dashed #000; padding-bottom: 10px; margin-bottom: 10px;">
            <h2 style="margin: 5px 0;">Bar DASHBOARD Analytics</h2>
            <p style="margin: 2px 0;">User: ${userId || 'N/A'}</p>
            <p style="margin: 2px 0;">Day: ${dayName}</p>
            <p style="margin: 2px 0;">Date Range: ${from} to ${to}</p>
            <p style="margin: 2px 0;">${now.toLocaleString()}</p>
          </div>
          
          <table style="width: 100%; border-collapse: collapse;">
            <thead>
              <tr style="border-bottom: 1px dashed #000;">
                <th style="text-align: left; padding: 5px 0; width: 10%;">S.No.</th>
                <th style="text-align: left; padding: 5px 0; width: 60%;">Item Name</th>
                <th style="text-align: right; padding: 5px 0; width: 30%;">Qty</th>
              </tr>
            </thead>
            <tbody>
              ${barSalesData.map((item, index) => `
                <tr>
                  <td style="padding: 3px 0; width: 10%;">${index + 1}</td>
                  <td style="padding: 3px 0; width: 60%;">${item.name || '-'}</td>
                  <td style="text-align: right; padding: 3px 0; width: 30%;">${parseFloat(item.qty || 0).toFixed(2)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          
          <div style="border-top: 1px dashed #000; padding-top: 10px; margin-top: 10px; text-align: right;">
            <p style="margin: 5px 0; font-weight: bold;">Total Qty: ${totalQty.toFixed(2)}</p>
          </div>
          
          <div style="text-align: center; margin-top: 20px; padding-top: 10px; border-top: 1px dashed #000;">
            <p style="margin: 2px 0; font-size: 10px;">Thank you for your business</p>
          </div>
        </div>
      `;
      
      const printWindow = window.open('', '', 'height=400,width=600');
      printWindow.document.write(printContent);
      printWindow.document.close();
      
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 250);
      
      toast.success('Print dialog opened');
    } catch (err) {
      console.error('Error printing:', err);
      toast.error('Error opening print dialog');
    }
  };

  if (accessDenied) {
    return (
      <div className="access-denied-container" style={{ padding: '20px' }}>
        <Alert
          title="Access Denied"
          description={
            <div>
              <p>This dashboard is only available for club store manager accounts.</p>
              <p>Your current role: <strong>{userType || 'Unknown'}</strong></p>
              <p>Please contact your administrator if you believe this is an error.</p>
            </div>
          }
          type="error"
          showIcon
          action={
            <div style={{ marginTop: '16px' }}>
              <Button 
                type="primary" 
                icon={<HomeOutlined />}
                onClick={() => navigate('/dashboard/admin')}
                style={{ marginRight: '8px' }}
              >
                Go to Dashboard
              </Button>
              <Button 
                onClick={() => {
                  localStorage.clear();
                  sessionStorage.clear();
                  navigate('/login');
                }}
              >
                Logout
              </Button>
            </div>
          }
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="dashboard-loading" style={{ padding: '40px', textAlign: 'center' }}>
        <Spin size="large" />
        <p style={{ marginTop: '20px' }}>Loading bar sales data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px' }}>
        <Alert
          title="Error"
          description={error}
          type="error"
          showIcon
          action={
            <Button onClick={() => fetchBarSalesData()} type="primary">
              Retry
            </Button>
          }
        />
      </div>
    );
  }

  const columns = [
    {
      title: 'S.No.',
      dataIndex: 'sno',
      key: 'sno',
      width: '10%',
      render: (_, __, index) => index + 1
    },
    {
      title: 'Item Name',
      dataIndex: 'name',
      key: 'name',
      width: '60%',
      render: (name) => name || '-'
    },
    {
      title: 'Quantity',
      dataIndex: 'qty',
      key: 'qty',
      width: '30%',
      render: (qty) => <span className="qty-value"><strong>{parseFloat(qty || 0).toFixed(2)}</strong></span>,
      align: 'right'
    }
  ];

  return (
    <>
      <ToastContainer />
      <Button
        type="primary"
        icon={<FileTextOutlined />}
        shape="circle"
        size="large"
        style={{ 
          position: 'fixed',
          right: 24, 
          bottom: 156,
          width: 56,
          height: 56,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          zIndex: 1000
        }}
        onClick={() => navigate('/report/clubstoremanager')}
        title="Generate Report"
      />
      <Button
        type="default"
        icon={<HomeOutlined />}
        shape="circle"
        size="large"
        style={{ 
          position: 'fixed',
          right: 24, 
          bottom: 90,
          width: 56,
          height: 56,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          zIndex: 1000
        }}
        onClick={() => navigate('/dashboard/clubstoremanager')}
        title="Dashboard"
      />
      <Button
        type="primary"
        icon={<LogoutOutlined />}
        shape="circle"
        size="large"
        danger
        style={{ 
          position: 'fixed',
          right: 24, 
          bottom: 24,
          width: 56,
          height: 56,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          zIndex: 1000
        }}
        onClick={handleLogout}
        title="Logout"
      />
      <div className="club-store-manager-dashboard" style={{ padding: '20px', minHeight: '100vh' }}>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Card title="Dashboard Analytics" className="report-card">
              {/* Date Range Selector */}
              <Row gutter={16} style={{ marginBottom: '20px' }}>
                <Col xs={24} sm={24} md={12} lg={12}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                    📅 Date Range
                  </label>
                  <RangePicker
                    value={dateRange}
                    onChange={handleDateRangeChange}
                    style={{ width: '100%' }}
                    format="YYYY-MM-DD"
                  />
                </Col>
                <Col xs={24} sm={24} md={12} lg={12}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                    📊 Summary
                  </label>
                  <div style={{ 
                    background: '#f0f2f5', 
                    padding: '12px', 
                    borderRadius: '4px',
                    textAlign: 'center'
                  }}>
                    <Text strong>Total Items Sold: </Text>
                    <Text style={{ fontSize: '18px', color: '#1890ff' }}>
                      <strong>{totalQty.toFixed(2)}</strong>
                    </Text>
                  </div>
                </Col>
              </Row>

              {/* Analytics Cards */}
              <Row gutter={16} style={{ marginBottom: '20px' }}>
                <Col xs={12} sm={12} md={6} lg={6}>
                  <Card bordered={false} style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
                    <Statistic
                      title={<span style={{ color: 'white' }}>Total Quantity</span>}
                      value={totalQty.toFixed(2)}
                      valueStyle={{ color: 'white' }}
                      prefix={<ShoppingCartOutlined />}
                    />
                  </Card>
                </Col>
                <Col xs={12} sm={12} md={6} lg={6}>
                  <Card bordered={false} style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
                    <Statistic
                      title={<span style={{ color: 'white' }}>Total Transactions</span>}
                      value={analytics.totalItems}
                      valueStyle={{ color: 'white' }}
                      prefix={<BarChartOutlined />}
                    />
                  </Card>
                </Col>
                <Col xs={12} sm={12} md={6} lg={6}>
                  <Card bordered={false} style={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
                    <Statistic
                      title={<span style={{ color: 'white' }}>Unique Items</span>}
                      value={analytics.uniqueItems}
                      valueStyle={{ color: 'white' }}
                      prefix={<RiseOutlined />}
                    />
                  </Card>
                </Col>
                <Col xs={12} sm={12} md={6} lg={6}>
                  <Card bordered={false} style={{ background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', color: 'white' }}>
                    <Statistic
                      title={<span style={{ color: 'white' }}>Avg per Transaction</span>}
                      value={analytics.totalItems > 0 ? (totalQty / analytics.totalItems).toFixed(2) : 0}
                      valueStyle={{ color: 'white' }}
                      prefix={<ShoppingCartOutlined />}
                    />
                  </Card>
                </Col>
              </Row>

              {/* Charts Row */}
              {barSalesData.length > 0 && (
                <Row gutter={16} style={{ marginBottom: '20px' }}>
                  <Col xs={24} sm={24} md={16} lg={16}>
                    <Card title="Top 10 Items by Quantity" bordered={false}>
                      <div style={{ height: '300px' }}>
                        <Bar data={topItemsChartData} options={chartOptions} />
                      </div>
                    </Card>
                  </Col>
                  <Col xs={24} sm={24} md={8} lg={8}>
                    <Card title="Category Distribution" bordered={false}>
                      <div style={{ height: '300px' }}>
                        {analytics.categoryData.length > 0 ? (
                          <Pie data={categoryChartData} options={pieChartOptions} />
                        ) : (
                          <div style={{ textAlign: 'center', paddingTop: '100px' }}>
                            <Text type="secondary">No category data available</Text>
                          </div>
                        )}
                      </div>
                    </Card>
                  </Col>
                </Row>
              )}

              {/* Export Buttons */}
              <Row gutter={16} style={{ marginBottom: '20px' }}>
                <Col xs={8} sm={6} md={4} lg={4}>
                  <Button 
                    type="primary" 
                    danger
                    block
                    icon={<FilePdfOutlined />}
                    onClick={exportToPDF}
                    loading={loading}
                  >
                    PDF
                  </Button>
                </Col>
                <Col xs={8} sm={6} md={4} lg={4}>
                  <Button 
                    type="primary" 
                    style={{ backgroundColor: '#10b981' }}
                    block
                    icon={<FileExcelOutlined />}
                    onClick={exportToExcel}
                    loading={loading}
                  >
                    Excel
                  </Button>
                </Col>
                <Col xs={8} sm={6} md={4} lg={4}>
                  <Button 
                    type="primary"
                    style={{ backgroundColor: '#f59e0b' }}
                    block
                    icon={<PrinterOutlined />}
                    onClick={printThermal}
                    loading={loading}
                  >
                    Print
                  </Button>
                </Col>
              </Row>

              {/* Additional Analytics Charts */}
              {barSalesData.length > 0 && (
                <>
                  {/* Daily Sales Trend */}
                  <Row gutter={16} style={{ marginBottom: '20px' }}>
                    <Col span={24}>
                      <Card title="Daily Sales Trend" bordered={false}>
                        <div style={{ height: '350px' }}>
                          <Line data={dailySalesTrendData} options={lineChartOptions} />
                        </div>
                      </Card>
                    </Col>
                  </Row>

                  {/* Category Breakdown & Item Distribution */}
                  <Row gutter={16} style={{ marginBottom: '20px' }}>
                    <Col xs={24} sm={24} md={12} lg={12}>
                      <Card title="Category Breakdown (Horizontal)" bordered={false}>
                        <div style={{ height: '350px' }}>
                          <Bar 
                            data={{
                              labels: analytics.categoryData.map(item => item.category),
                              datasets: [
                                {
                                  label: 'Quantity',
                                  data: analytics.categoryData.map(item => item.quantity),
                                  backgroundColor: 'rgba(153, 102, 255, 0.6)',
                                  borderColor: 'rgba(153, 102, 255, 1)',
                                  borderWidth: 1
                                }
                              ]
                            }} 
                            options={{
                              ...chartOptions,
                              indexAxis: 'y',
                            }} 
                          />
                        </div>
                      </Card>
                    </Col>
                    <Col xs={24} sm={24} md={12} lg={12}>
                      <Card title="Top 5 Items (Doughnut)" bordered={false}>
                        <div style={{ height: '350px' }}>
                          <Doughnut 
                            data={{
                              labels: analytics.topItems.slice(0, 5).map(item => item.name.length > 15 ? item.name.substring(0, 15) + '...' : item.name),
                              datasets: [
                                {
                                  label: 'Quantity',
                                  data: analytics.topItems.slice(0, 5).map(item => item.quantity),
                                  backgroundColor: [
                                    'rgba(255, 99, 132, 0.8)',
                                    'rgba(54, 162, 235, 0.8)',
                                    'rgba(255, 206, 86, 0.8)',
                                    'rgba(75, 192, 192, 0.8)',
                                    'rgba(153, 102, 255, 0.8)'
                                  ],
                                  borderColor: [
                                    'rgba(255, 99, 132, 1)',
                                    'rgba(54, 162, 235, 1)',
                                    'rgba(255, 206, 86, 1)',
                                    'rgba(75, 192, 192, 1)',
                                    'rgba(153, 102, 255, 1)'
                                  ],
                                  borderWidth: 2
                                }
                              ]
                            }} 
                            options={pieChartOptions} 
                          />
                        </div>
                      </Card>
                    </Col>
                  </Row>
                </>
              )}

              {barSalesData.length === 0 && (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <Text type="secondary" style={{ fontSize: '16px' }}>No data available for the selected date range</Text>
                </div>
              )}
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default ClubStoreManagerDashboard;
